import { Dark } from "quasar";
import { ref } from "vue";

/**
 * Light / dark / system, persisted per browser.
 *
 * Three states rather than a boolean, matching the CTF editor UI: "system"
 * tracks the OS and is not the same as having picked light or dark, because it
 * keeps following later OS changes.
 *
 * The key is read twice — here, and by the inline script in index.html that
 * runs before Vue boots so the first paint is already the right colour. Keep
 * the two in step.
 */

export type ColorScheme = "light" | "dark" | "system";

export const COLOR_SCHEME_KEY = "ctf_workflow_color_scheme";

const SCHEMES: ColorScheme[] = ["light", "dark", "system"];

export function readStoredScheme(): ColorScheme {
  try {
    const raw = localStorage.getItem(COLOR_SCHEME_KEY);
    if (raw !== null && (SCHEMES as string[]).includes(raw)) {
      return raw as ColorScheme;
    }
  } catch {
    // Private browsing or blocked storage — fall through to the default.
  }
  return "system";
}

/** Quasar owns the `body--dark` class; "auto" makes it follow the OS. */
export function applyScheme(scheme: ColorScheme): void {
  Dark.set(scheme === "system" ? "auto" : scheme === "dark");
}

const scheme = ref<ColorScheme>(readStoredScheme());

export function useColorScheme() {
  function setScheme(next: ColorScheme): void {
    scheme.value = next;
    try {
      localStorage.setItem(COLOR_SCHEME_KEY, next);
    } catch {
      // Not persisting is survivable; the session still honours the choice.
    }
    applyScheme(next);
  }

  return { scheme, setScheme };
}
